import styled from '@emotion/styled';
import { FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';

import { SortingObject } from '@/__configs/config_types';
import useColumns from '@/components/_hooks/useColumns';
import { ConditionalTooltip, PolarisIcon } from '@/exports/columnCommonComponents';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type AdvanceSortListItemProps = {
  index: number;
  selectedColumn: string;
  selectedDirection: 'asc' | 'desc';
  sortFields: SortingObject[];
  onRemoveSort: (index: number) => void;
  onSortingChange: (index: number, id: SortingObject['id'], order: SortingObject['order']) => void;
};

function AdvanceSortListItem({ index, selectedColumn, selectedDirection, sortFields, onSortingChange, onRemoveSort }: AdvanceSortListItemProps) {
  const { advanceSortColumns } = useColumns();

  const handleColumnChange = (event: SelectChangeEvent) => {
    onSortingChange(index, event.target.value, selectedDirection);
  };

  const handleDirectionChange = (event: SelectChangeEvent) => {
    onSortingChange(index, selectedColumn, event.target.value as 'asc' | 'desc');
  };

  const allowedColumns = advanceSortColumns.filter(
    (col) => !sortFields.find((sortField) => sortField.id !== selectedColumn && sortField.id === (col?.sortField || col.field))
  );
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: selectedColumn });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return (
    <StyledGrid ref={setNodeRef} style={style} {...attributes}>
      <DragIndicator {...listeners} color="primary" style={{ cursor: 'pointer' }} />
      <StyledFormControl variant="filled">
        <InputLabel>Column</InputLabel>
        <Select value={selectedColumn} label="Column" onChange={handleColumnChange}>
          <MenuItem disabled value="">
            <em>Select a column</em>
          </MenuItem>
          {allowedColumns.map((column) => (
            <MenuItem key={column.id} value={column?.sortField || column.field}>
              {column.id === 'priority' ? 'Priority' : column.label}
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>
      <StyledFormControl variant="filled">
        <InputLabel>Direction</InputLabel>
        <Select value={selectedDirection} label="Direction" onChange={handleDirectionChange}>
          <MenuItem disabled value="">
            <em>Select a direction</em>
          </MenuItem>
          <MenuItem key="asc" value="asc">
            Ascending
          </MenuItem>
          <MenuItem key="desc" value="desc">
            Descending
          </MenuItem>
        </Select>
      </StyledFormControl>
      <ConditionalTooltip show={sortFields.length > 1} title="Remove sort">
        <DeleteIcon disabled={sortFields.length === 1} onClick={() => onRemoveSort(index)}>
          <PolarisIcon icon="close" />
        </DeleteIcon>
      </ConditionalTooltip>
    </StyledGrid>
  );
}

export default AdvanceSortListItem;

const StyledGrid = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr 1fr auto',
  alignItems: 'center',
  columnGap: theme.spacing(1),
  backgroundColor: theme.palette.background.paper
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '.MuiFilledInput-root, .MuiFilledInput-input:focus': {
    backgroundColor: theme.palette.background.default
  }
}));

const DeleteIcon = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey[500],
  '&:disabled': {
    pointerEvents: 'auto',
    cursor: 'not-allowed'
  }
}));
